import {
  isSupportedLocale,
  type SupportedLocale,
} from '@brightdrop/localization-core';

/** sessionStorage key holding the language chosen from the console. */
export const LANGUAGE_OVERRIDE_STORAGE_KEY = 'cspLanguageOverride';

type Listener = () => void;

const listeners = new Set<Listener>();
let isGateOpen = false;

const notify = () => {
  for (const listener of listeners) {
    listener();
  }
};

// Storage can be unavailable (privacy modes, sandboxed frames). The override is a test aid, so
// it simply does not apply there.
const readSession = (): string | null => {
  try {
    return globalThis.sessionStorage?.getItem(LANGUAGE_OVERRIDE_STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * Reads the stored console language, whether or not the gate is open
 * @returns The stored locale, or null when none is stored or it is not supported
 */
export const readStored = (): SupportedLocale | null => {
  const stored = readSession() ?? undefined;
  return isSupportedLocale(stored) ? stored : null;
};

/**
 * Stores a console language for this tab and notifies subscribers
 * @param locale - A supported locale
 */
export const setStored = (locale: SupportedLocale): void => {
  try {
    globalThis.sessionStorage?.setItem(LANGUAGE_OVERRIDE_STORAGE_KEY, locale);
  } catch {
    return;
  }
  notify();
};

/** Removes the console language for this tab and notifies subscribers. */
export const clearStored = (): void => {
  try {
    globalThis.sessionStorage?.removeItem(LANGUAGE_OVERRIDE_STORAGE_KEY);
  } catch {
    return;
  }
  notify();
};

/**
 * Opens or closes the gate (environment and feature flag). A stored language applies only while
 * the gate is open.
 * @param open - Whether the override may apply
 */
export const setGateOpen = (open: boolean): void => {
  if (isGateOpen === open) return;
  isGateOpen = open;
  notify();
};

/**
 * Subscribes to override changes, in the shape `useSyncExternalStore` expects
 * @param listener - Called whenever the stored language or the gate changes
 * @returns A function that removes the listener
 */
export const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * The language the UI should use instead of the profile's
 * @returns The stored locale while the gate is open, otherwise null
 */
export const getActiveOverride = (): SupportedLocale | null =>
  isGateOpen ? readStored() : null;
